import { useContext, ref, reactive, computed } from "@nuxtjs/composition-api";
import useVuelidate from "@vuelidate/core";
import { sameAs } from "@vuelidate/validators";
import { useAuthStore } from "../auth";
import CREATE_CUSTOMER from "./createCustomer.graphql";
import errorCodes from "~/helpers/errorCodes";

const initialFormData = () => ({
  email: "",
  password: "",
  confirmPassword: "",
  termsAndConditionsAccepted: false,
});

export function useCustomerRegistrationForm() {
  const {
    $validation: { rules, withI18nMessage },
    $mutate,
    i18n,
    $joszaki,
    $logger,
  } = useContext();
  const submitting = ref(false);
  const requestError = ref(null);
  const authStore = useAuthStore();
  const formData = reactive(initialFormData());

  const resetForm = () => {
    Object.assign(formData, initialFormData());
  };

  const validationArgs = computed(() => ({
    email: {
      required: rules.required,
      email: rules.email,
      maxLength: rules.maxLength(100),
    },
    password: {
      required: rules.required,
      passwordFormat: rules.passwordFormat(),
    },
    confirmPassword: {
      required: rules.required,
      sameAs: withI18nMessage(sameAs(formData.password), {
        messagePath: () => "customerRegistration.form.passwordsDontMatch",
      }),
    },
    termsAndConditionsAccepted: {
      consentRequired: rules.consentRequired(
        i18n.t("blogPage.acceptPrivacyPolicyErrorMessage")
      ),
    },
  }));
  const v$ = useVuelidate(validationArgs, formData);

  async function submitRegistrationForm() {
    try {
      submitting.value = true;
      requestError.value = null;

      const isValid = await v$.value.$validate();
      if (!isValid) {
        return false;
      }

      const { token } = await $mutate(CREATE_CUSTOMER, {
        // TODO: we don't store the fact that the user has accepted terms and conditions in the database.
        email: formData.email,
        password: formData.password,
      });

      await authStore.loginWithToken(token);

      $joszaki.toast({
        type: "success",
        message: i18n.t("customerRegistration.form.successMessage"),
      });
      return true;
    } catch (e) {
      $logger.error(errorCodes.UNKNOWN_ERROR_CUSTOMER_REGISTRATION, e);

      // Ismert hibaüzenetek kezelése
      if (typeof e === "string") {
        requestError.value = e;
        return;
      }

      requestError.value = i18n.t("error.unknown");
      $joszaki.toast({
        type: "error",
        message: i18n.t("customerRegistration.form.errorMessage"),
      });
      return false;
    } finally {
      submitting.value = false;
    }
  }

  return {
    submitting,
    formData,
    resetForm,
    v$,
    requestError,
    submitRegistrationForm,
  };
}
